import React from 'react';
import { format } from 'date-fns';
import {
  getAccommodationDisplay,
  getPrimaryCourse,
  getCourseCount,
  getGuestCount,
  getAllRooms,
  getAllBeds
} from '../../utils/bookingHelpers';

/**
 * Mobile-optimized Booking Card Component
 * Compact design for small screens with a uniform single-card row
 */
function MobileBookingCard({ 
  booking, 
  onEdit,
  getStatusBadge,
  getPaymentStatusBadge
}) {
  const isBookingEngine = booking.bookingPlatform === 'BOOKING_ENGINE';
  
  return (
    <div 
      onClick={() => onEdit(booking)}
      style={{
        backgroundColor: isBookingEngine ? '#E1E9E2' : 'white',
        border: isBookingEngine ? '1px solid #9DB7A5' : '1px solid #E3DFD3',
        borderRadius: '12px',
        padding: '14px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        position: 'relative',
        width: '100%',
        maxWidth: '100%',
        minHeight: '176px',
        boxSizing: 'border-box',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}
    >
      {/* Status badges - compact horizontal layout */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '8px',
        gap: '6px',
        minWidth: 0
      }}>
        <div style={{ fontSize: '10px', minWidth: 0, overflow: 'hidden' }}>
          {getStatusBadge(booking.status)}
        </div>
        <div style={{ fontSize: '10px', minWidth: 0, overflow: 'hidden' }}>
          {getPaymentStatusBadge(booking.paymentStatus)}
        </div>
      </div>

      {/* Guest Info - Compact */}
      <div style={{ marginBottom: '8px' }}>
        <h4 style={{ 
          margin: '0 0 2px 0', 
          fontSize: '14px', 
          fontWeight: '700', 
          color: '#1A3E2A',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {booking.user?.name || booking.guestName || 'N/A'}
        </h4>
        <p style={{ 
          margin: '0', 
          fontSize: '11px', 
          color: '#5F6B61',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {booking.user?.email || booking.guestEmail || 'N/A'}
        </p>
      </div>

      {/* Accommodation - Very Compact */}
      <div style={{ 
        backgroundColor: '#F5F3EB', 
        padding: '8px', 
        borderRadius: '8px', 
        marginBottom: '8px',
        border: '1px solid #E3DFD3',
        minWidth: 0
      }}>
        <div style={{ 
          fontSize: '12px', 
          fontWeight: '600', 
          color: '#24332A',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {getAccommodationDisplay(booking)}
        </div>
        
        {/* Multi-entity indicator */}
        {(() => {
          const rooms = getAllRooms(booking);
          const beds = getAllBeds(booking);
          const courseCount = getCourseCount(booking);
          const guestCount = getGuestCount(booking);
          
          if (rooms.length > 1 || beds.length > 1 || courseCount > 1 || guestCount > 1) {
            return (
              <div style={{ 
                display: 'flex', 
                gap: '8px', 
                marginTop: '4px',
                fontSize: '10px',
                color: '#5F6B61',
                flexWrap: 'wrap'
              }}>
                {rooms.length > 1 && <span>{rooms.length}</span>}
                {beds.length > 1 && <span>{beds.length}</span>}
                {guestCount > 1 && <span>{guestCount}</span>}
                {courseCount > 1 && <span>{courseCount}</span>}
              </div>
            );
          }
          return null;
        })()}
      </div>

      {/* Dates - Compact grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr', 
        gap: '8px',
        marginBottom: '8px'
      }}>
        <div>
          <label style={{ 
            display: 'block', 
            fontSize: '10px', 
            fontWeight: '600', 
            color: '#5F6B61', 
            marginBottom: '2px' 
          }}>
            Check-in
          </label>
          <div style={{ fontSize: '11px', fontWeight: '600', color: '#1A3E2A' }}>
            {booking.checkIn ? format(new Date(booking.checkIn), 'MMM dd') : 'N/A'}
          </div>
        </div>
        <div>
          <label style={{ 
            display: 'block', 
            fontSize: '10px', 
            fontWeight: '600', 
            color: '#5F6B61', 
            marginBottom: '2px' 
          }}>
            Check-out
          </label>
          <div style={{ fontSize: '11px', fontWeight: '600', color: '#1A3E2A' }}>
            {booking.checkOut ? format(new Date(booking.checkOut), 'MMM dd') : 'N/A'}
          </div>
        </div>
      </div>

      {/* Price */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        paddingTop: '8px',
        borderTop: '1px solid #E3DFD3',
        marginTop: 'auto',
        gap: '8px'
      }}>
        <span style={{ fontSize: '11px', color: '#5F6B61', fontWeight: '600' }}>
          Total:
        </span>
        <span style={{ fontSize: '14px', fontWeight: '700', color: '#1A3E2A', whiteSpace: 'nowrap' }}>
          ₹{booking.totalPrice || booking.finalAmount || 0}
        </span>
      </div>
    </div>
  );
}

export default MobileBookingCard;
